import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FiHome,
  FiUser,
  FiBriefcase,
  FiLayers,
  FiFolder,
  FiGrid,
  FiMail,
  FiSun,
  FiMoon,
} from "react-icons/fi";
import { useLenis } from "../SmoothScroll";

const ITEMS = [
  { id: "hero", label: "Home", icon: FiHome },
  { id: "about", label: "About", icon: FiUser },
  { id: "experience", label: "Experience", icon: FiBriefcase },
  { id: "skills", label: "Skills", icon: FiLayers },
  { id: "portfolio", label: "Work", icon: FiFolder },
  { id: "services", label: "Services", icon: FiGrid },
  { id: "contact", label: "Contact", icon: FiMail },
];

const Navbar = () => {
  const lenis = useLenis();
  const [active, setActive] = useState("hero");
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
  }, []);

  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", next);
    try {
      localStorage.setItem("theme", next ? "dark" : "light");
    } catch (e) {
      /* ignore */
    }
    setDark(next);
  };

  useEffect(() => {
    const sections = ITEMS.map((i) => document.getElementById(i.id)).filter(
      Boolean
    );
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { duration: 1.3 });
    else el.scrollIntoView({ behavior: "smooth" });
    setActive(id);
  };

  return (
    <motion.nav
      initial={{ y: 80, opacity: 0, x: "-50%" }}
      animate={{ y: 0, opacity: 1, x: "-50%" }}
      transition={{ delay: 0.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-5 left-1/2 z-50"
    >
      <ul className="flex items-center gap-1 rounded-full border border-hairline bg-[color:var(--card)] p-1.5 shadow-glass backdrop-blur-2xl">
        {ITEMS.map(({ id, label, icon: Icon }) => {
          const isActive = active === id;
          return (
            <li key={id} className="group relative">
              <a
                href={`#${id}`}
                onClick={(e) => go(e, id)}
                aria-label={label}
                aria-current={isActive ? "true" : undefined}
                className="relative flex h-11 w-11 items-center justify-center rounded-full text-lg transition-colors duration-300"
              >
                {isActive && (
                  <motion.span
                    layoutId="nav-active"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className="absolute inset-0 rounded-full bg-content shadow-glow"
                  />
                )}
                <Icon
                  className={`relative z-10 transition-colors duration-300 ${
                    isActive
                      ? "text-surface"
                      : "text-muted group-hover:text-content"
                  }`}
                />
              </a>

              {/* tooltip */}
              <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md bg-content px-2.5 py-1 text-xs text-surface opacity-0 backdrop-blur-md transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
                {label}
              </span>
            </li>
          );
        })}

        <li aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-hairline" />

        <li className="group relative">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            className="relative flex h-11 w-11 items-center justify-center rounded-full text-lg text-muted transition-colors duration-300 hover:text-content"
          >
            {dark ? <FiSun /> : <FiMoon />}
          </button>
          <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md bg-content px-2.5 py-1 text-xs text-surface opacity-0 backdrop-blur-md transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
            {dark ? "Light" : "Dark"}
          </span>
        </li>
      </ul>
    </motion.nav>
  );
};

export default Navbar;
